import { createReducer } from 'src/helpers/reducer';
import { ISendRequestParams } from '@dash/actions';
import { isEmpty, formatMobilePhone, isEmail, isSSN } from '@jjj/components';
import { t } from 'src/i18n';
import { isMobilePhone } from 'src/helpers/validators';
import { isUSA, isPostalCode } from 'src/helpers/countriesHelpers';
import actionsTypes from './actionTypes';
import {
  IProcessActionSuccessPayload,
  IBulkUploadWorkerModalState,
  Attribute,
  IFieldTypes,
  IExtendedChangePropertyPayload,
  IChangePositionPayload,
  IChangeTierPayload,
  IProcessActionErrorPayload,
  IRemoveSkillPayload,
} from './types';

function validate(attr: Attribute, value: IFieldTypes, params?: any) {
  let errorMessage = '';

  const requiredAttributes: Attribute[] = [
    'first_name',
    'last_name',
    'birthday',
    'ssn',
    'phone_number',
    'email',
    'street',
    'city',
    'zip',
    'state',
  ];

  if (attr === 'zip') {
    if (value && !isPostalCode(String(value))) {
      errorMessage = t('dash.validation.valid_error', {
        fieldName: t('dash.talent.bulk_upload_workers.modal.individual_tab.zip_code_label'),
      });
    }
  } else if (isUSA() && attr === 'ssn' && !isSSN(value)) {
    errorMessage = t('dash.validation.valid_error', { fieldName: t('dash.talent.social_security_number_label') });
  } else if (attr === 'phone_number') {
    if (!isMobilePhone(formatMobilePhone(value), params?.phoneNumberCountry)) {
      errorMessage = t('dash.validation.valid_error', { fieldName: t('dash.profile.phone_label') });
    }
  } else if (attr === 'email' && !isEmail(value)) {
    errorMessage = t('dash.validation.valid_error', {
      fieldName: t('dash.talent.bulk_upload_workers.modal.individual_tab.email_label'),
    });
  } else if (requiredAttributes.includes(attr)) {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t(`dash.talent.bulk_upload_workers.modal.individual_tab.${attr}_label`),
      });
    }
  }

  return errorMessage;
}

const getInitialField = (fieldName: Attribute, value: string | null = null) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || ''),
});

const initialIndividualEditForm = {
  first_name: getInitialField('first_name'),
  last_name: getInitialField('last_name'),
  birthday: getInitialField('birthday'),
  ssn: getInitialField('ssn'),
  phone_number: getInitialField('phone_number'),
  email: getInitialField('email'),
  street: getInitialField('street'),
  city: getInitialField('city'),
  zip: getInitialField('zip'),
  state: getInitialField('state'),
  hired_date: getInitialField('hired_date'),
  gender: getInitialField('gender'),
  skills: [
    {
      position: getInitialField('position'),
      tier: getInitialField('tier'),
    },
  ],
};

const initialBulkUploadEditForm = {
  file: getInitialField('file'),
};

const initState: IBulkUploadWorkerModalState = {
  isFormLoading: false,
  isModalVisible: false,
  isCreateWorkerLoading: false,
  isBulkUploadWorkersLoading: false,
  isTemplateDownloading: false,
  individualEditForm: initialIndividualEditForm,
  bulkUploadEditForm: initialBulkUploadEditForm,
  formData: undefined,
};

function onChangeModalVisibility(state: IBulkUploadWorkerModalState) {
  return {
    ...state,
    isModalVisible: !state.isModalVisible,
  };
}

function onProcessActionRequest(state: IBulkUploadWorkerModalState, { payload }: { payload: ISendRequestParams }) {
  return {
    ...state,
    isCreateWorkerLoading: payload.key === 'create_worker' ? true : state.isFormLoading,
    isFormLoading: payload.key === 'create_form_data' ? true : state.isFormLoading,
    isTemplateDownloading: payload.key === 'template' ? true : state.isTemplateDownloading,
  };
}

function onProcessActionSuccess(
  state: IBulkUploadWorkerModalState,
  { payload }: { payload: IProcessActionSuccessPayload },
) {
  const { formData, key } = payload;

  return {
    ...state,
    isLoading: false,
    isFormLoading: false,
    isCreateWorkerLoading: false,
    isTemplateDownloading: false,
    formData: formData || state.formData,
    isModalVisible: key === 'create_worker' ? false : state.isModalVisible,
  };
}

function onProcessActionError(
  state: IBulkUploadWorkerModalState,
  { payload }: { payload: IProcessActionErrorPayload },
) {
  const { individualEditForm } = state;

  if (payload.errors && payload.key === 'create_worker') {
    const { errors } = payload;

    Object.keys(errors).forEach(key => {
      if (key === 'state_id') {
        individualEditForm.state.error = errors[key];
        individualEditForm.state.touched = true;
      } else {
        // @ts-ignore
        individualEditForm[key].error = errors[key];
        // @ts-ignore
        individualEditForm[key].touched = true;
      }
    });
  }

  return {
    ...state,
    isCreateWorkerLoading: false,
    isFormLoading: false,
    isTemplateDownloading: false,
  };
}

function onChangeProperty(
  state: IBulkUploadWorkerModalState,
  { payload }: { payload: IExtendedChangePropertyPayload },
): IBulkUploadWorkerModalState {
  const { formName, attr, value, params } = payload;

  const editForm = state[formName];

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val, params),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    [formName]: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onAddNewSkill(state: IBulkUploadWorkerModalState) {
  const { individualEditForm } = state;

  const newSkill = {
    position: getInitialField('position'),
    tier: getInitialField('tier'),
  };

  if (individualEditForm.skills) {
    individualEditForm.skills.push(newSkill);
  } else {
    individualEditForm.skills = [newSkill];
  }

  return {
    ...state,
  };
}

function onChangePosition(state: IBulkUploadWorkerModalState, { payload }: { payload: IChangePositionPayload }) {
  const { individualEditForm } = state;
  const { position, skillIndex } = payload;

  individualEditForm.skills[skillIndex].position.value = position;
  individualEditForm.skills[skillIndex].position.touched = true;
  individualEditForm.skills[skillIndex].tier.value = null;
  individualEditForm.skills[skillIndex].tier.touched = true;
  individualEditForm.skills[skillIndex].tier.error = t('dash.validation.required_error', {
    fieldName: t('dash.talent.bulk_upload_workers.modal.individual_tab.tier_label'),
  });

  return {
    ...state,
    individualEditForm,
  };
}

function onChangeTier(state: IBulkUploadWorkerModalState, { payload }: { payload: IChangeTierPayload }) {
  const { individualEditForm } = state;
  const { tier, skillIndex } = payload;

  individualEditForm.skills[skillIndex].tier.value = tier;
  individualEditForm.skills[skillIndex].tier.touched = true;
  individualEditForm.skills[skillIndex].tier.error = null;

  return {
    ...state,
    individualEditForm,
  };
}

function onClearState() {
  return {
    ...initState,
    individualEditForm: {
      ...initState.individualEditForm,
      skills: [
        {
          position: getInitialField('position'),
          tier: getInitialField('tier'),
        },
      ],
    },
  };
}

function onBulkUploadWorkersRequest(state: IBulkUploadWorkerModalState) {
  return {
    ...state,
    isBulkUploadWorkersLoading: true,
  };
}
function onBulkUploadWorkersSuccess(state: IBulkUploadWorkerModalState) {
  return {
    ...state,
    isBulkUploadWorkersLoading: false,
    isModalVisible: false,
    individualEditForm: initialIndividualEditForm,
    bulkUploadEditForm: initialBulkUploadEditForm,
  };
}
function onBulkUploadWorkersError(state: IBulkUploadWorkerModalState) {
  return {
    ...state,
    isBulkUploadWorkersLoading: false,
  };
}

function onRemoveSkill(state: IBulkUploadWorkerModalState, { payload }: { payload: IRemoveSkillPayload }) {
  const { individualEditForm } = state;
  const { skillIndex } = payload;

  if (individualEditForm.skills.length === 1 && skillIndex === 0) {
    individualEditForm.skills[skillIndex].position.value = null;
    individualEditForm.skills[skillIndex].position.error = '';
    individualEditForm.skills[skillIndex].tier.value = null;
    individualEditForm.skills[skillIndex].tier.error = '';
  } else {
    individualEditForm.skills.splice(skillIndex, 1);
  }

  return {
    ...state,
    individualEditForm,
  };
}

export default createReducer(initState, {
  [actionsTypes.CHANGE_MODAL_VISIBILITY]: onChangeModalVisibility,
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.PROCESS_ACTION_SUCCESS]: onProcessActionSuccess,
  [actionsTypes.PROCESS_ACTION_ERROR]: onProcessActionError,
  [actionsTypes.ADD_NEW_SKILL]: onAddNewSkill,
  [actionsTypes.CHANGE_POSITION]: onChangePosition,
  [actionsTypes.CHANGE_TIER]: onChangeTier,
  [actionsTypes.BULK_UPLOAD_WORKERS_REQUEST]: onBulkUploadWorkersRequest,
  [actionsTypes.BULK_UPLOAD_WORKERS_SUCCESS]: onBulkUploadWorkersSuccess,
  [actionsTypes.BULK_UPLOAD_WORKERS_ERROR]: onBulkUploadWorkersError,
  [actionsTypes.REMOVE_SKILL]: onRemoveSkill,
  [actionsTypes.CLEAR_STATE]: onClearState,
});
